import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ActionsService } from '@core/actions-modals/actions.service';
import { AbsenceActionsService } from '@features/absence/absence-actions.service';
import { AbsenceService } from '@features/absence/absence.service';
import { Permissions } from '@features/absence/overview/requests-list/permissions';
import { AbsenceRequestsSortFields } from '@features/absence/overview/requests-list/requests-sort-items';
import { requestStatuses } from '@features/absence/overview/requests-list/statuses.enum';
import { AuthService } from '@features/auth/auth.service';
import { UserRoles } from '@features/auth/user-roles';
import { TranslateService } from '@ngx-translate/core';
import { EntityEditService } from '@services/common/entity-edit.service';
import { TimeService } from '@services/common/time.service';
import { IActionModel } from '@ui-kit/actions-tile/actions-tile.component';
import { ToasterService } from '@ui-kit/app-toast/toaster.service';
import { CollectionModel } from 'collection';
import { format } from 'date-fns';
import { of } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';

import { legacyParse, convertTokens } from '@date-fns/upgrade/v2';

marker('Are you sure you want to set this request as approved?');
marker('Are you sure you want to set this request as canceled?');
marker('Are you sure you want to set these requests as approved?');
marker('Are you sure you want to set these requests as canceled?');

export enum StatusTypesEnum {
  Approved = 'approved',
  Canceled = 'canceled'
}

@Component({
  selector: 'absence-requests-list',
  templateUrl: './requests-list.component.html',
  styleUrls: ['./requests-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RequestsListComponent implements OnInit, OnDestroy {
  collection: CollectionModel = this._absenceService.absenceRequestsCollection;
  sortFields = AbsenceRequestsSortFields;
  requestStatuses = requestStatuses;

  isAdmin: boolean = this._auth.isAdmin();

  actions: Array<IActionModel> = [
    {
      name: 'Approve',
      icon: 'icon-Save_ico',
      show: true,
      click: () => {
        const selectedArray = Array.from(this.collection.selectedItems);
        this._absenceActionsService
          .updateAbsenceVacationMany(selectedArray, StatusTypesEnum.Approved)
          .pipe(
            take(1),
            tap(() => this.collection.search())
          )
          .subscribe();
      }
    },
    {
      name: 'Delete',
      icon: 'icon-Delete_ico',
      actionClass: 'delete-action',
      show: true,
      click: () => {
        const selectedArray = Array.from(this.collection.selectedItems);
        this._absenceActionsService
          .deleteAbsenceVacationMany(selectedArray)
          .pipe(
            take(1),
            tap(() => this.collection.search())
          )
          .subscribe();
      }
    }
  ];

  handleSelect(e, item: any): void {
    e.preventDefault();
    e.stopPropagation();
    this.collection.selectItem(item);
  }

  setPeriodTooltip({ from, to, working_days }: { from: string; to: string; working_days: number }): string {
    let period = this._translate.instant('day');

    if (working_days > 1) {
      period = this._translate.instant('days');
    }

    return (
      format(legacyParse(from), convertTokens('DD.MM.YYYY')) +
      ' - ' +
      format(legacyParse(to), convertTokens('DD.MM.YYYY')) +
      ' ' +
      `(${working_days + ' ' + period})`
    );
  }

  closeActions(e): void {
    this.collection.unSelectAll();
  }

  editAbsence(requestItem): void {
    if (this.canUpdate(requestItem)) {
      const updatedAction$ = (item) => of(item).pipe(tap(() => this.collection.search()));

      const deletedAction$ = (item) => of(item).pipe(tap(() => this.delete(item)));

      this._entityService
        .openAbsenceModal({ id: requestItem.id })
        .pipe(
          take(1),
          switchMap(({ item, action }) => {
            if (action === 'update') {
              return updatedAction$(item);
            } else if (action === 'delete') {
              return deletedAction$(item);
            }
          })
        )
        .subscribe();
    }
  }

  getPeriodDays(from, to) {
    return this._timeService.periodDays(from, to);
  }

  changeStatus(item, action: requestStatuses) {
    this._absenceActionsService
      .changeAbsenceVacationStatus(item, action)
      .pipe(
        take(1),
        tap(() => this.collection.search())
      )
      .subscribe();
  }

  canDelete(item): boolean {
    const currentRole = this._auth.currentRole;

    if (currentRole === UserRoles.Admin) {
      return this._auth.can(Permissions.delete);
    } else if (currentRole === UserRoles.Manager) {
      const authUserId = this._auth.currentUser.id;
      return this._auth.can(Permissions.delete) && item.user_id === authUserId;
    }
  }

  canUpdate(item): boolean {
    const currentRole = this._auth.currentRole;

    if (currentRole === UserRoles.Admin) {
      return this._auth.can(Permissions.update);
    } else if (currentRole === UserRoles.Manager) {
      const authUserId = this._auth.currentUser.id;
      return this._auth.can(Permissions.update) && item.user_id === authUserId;
    }
  }

  delete(item): void {
    this._absenceActionsService
      .deleteAbsenceVacation(item)
      .pipe(
        take(1),
        tap(() => this.collection.search())
      )
      .subscribe();
  }

  private initFields(): void {
    this.sortFields = Object.assign([], AbsenceRequestsSortFields);

    this.sortFields.filter((item, index) => {
      if (!this.canUpdate(item) && item.templateRefKey === 'fieldOptions') {
        this.sortFields.splice(index, 1);
      }
    });

    this.collection.search();
  }

  private setMultiselectPermissions(): void {
    const isAdmin = this._auth.isAdmin();
    if (!isAdmin) {
      this.actions[1].show = false;
      this.actions[2].show = false;
    }
  }

  ngOnInit(): void {
    this.initFields();
    this.setMultiselectPermissions();
  }

  ngOnDestroy() {
    this.collection.clear();
  }

  constructor(
    private _absenceService: AbsenceService,
    private _timeService: TimeService,
    private _absenceActionsService: AbsenceActionsService,
    private _translate: TranslateService,
    private _toastService: ToasterService,
    private _actionsService: ActionsService,
    private _auth: AuthService,
    private _entityService: EntityEditService
  ) {}
}
